/**
 * "Your employer account is pending" — shown after signup and on any gated
 * route the employer visits before an admin approves them. Displays what
 * they submitted so they can double-check the details we're vetting.
 *
 * ADDS Feb 2026: "Verify with LinkedIn" flow.
 *   · Button hits POST /api/employer/linkedin/start → gets auth_url →
 *     window.location.href = auth_url (LinkedIn OIDC consent screen).
 *   · LinkedIn redirects to /api/employer/linkedin/callback which does
 *     the state check + token exchange + auto-verify decision, then
 *     302s back to /employer/pending?linkedin=<status>&reason=<why>.
 *   · Toast reads the query params + shows success/failure + auto-refresh
 *     to /discover on `verified`.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Clock, ShieldCheck, LogOut, Linkedin, CheckCircle2, AlertCircle, Loader } from "lucide-react";
import axios from "axios";
import { useEmployer } from "../components/employer/EmployerProtected";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Human strings for the callback ?reason= values so the pending page can
// give the employer a clear next step (not a raw slug).
const REASON_COPY = {
  email_match:              "Your LinkedIn email matched your signup — welcome!",
  domain_match:             "Your LinkedIn email domain matched your company — welcome!",
  no_match:                 "Your LinkedIn identity was accepted but the emails didn't match. An admin will finish the review shortly.",
  invalid_or_expired_state: "Session expired — please click Verify again.",
  missing_code_or_state:    "LinkedIn didn't send the expected response. Please retry.",
  admin_impersonating:      "Can't verify while browsing as another employer. Exit impersonation first.",
  linkedin_not_configured:  "LinkedIn verification is temporarily unavailable. Please retry later.",
  linkedin_unreachable:     "Couldn't reach LinkedIn. Please retry in a minute.",
  no_access_token:          "LinkedIn didn't return an access token. Please retry.",
  userinfo_unreachable:     "LinkedIn's profile service is unreachable. Please retry.",
};

function reasonMessage(reason) {
  if (!reason) return "";
  return REASON_COPY[reason] || reason.replace(/_/g, " ");
}

export default function EmployerPending() {
  const { employer, loading, refetch } = useEmployer();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [ liBusy, setLiBusy ] = useState(false);
  const [ liError, setLiError ] = useState("");

  const linkedinStatus = params.get("linkedin");
  const linkedinReason = params.get("reason") || "";
  const bannerCopy = reasonMessage(linkedinReason);

  // If the callback flipped us to verified, hop straight to /discover.
  useEffect(() => {
    if (!loading && employer?.status === "verified") {
      const t = setTimeout(() => { navigate("/discover", { replace: true }); }, 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [loading, employer?.status, navigate]);

  // If we came back from LinkedIn with linkedin=verified, refresh the
  // employer session so `employer.status` flips in-place (the redirect
  // useEffect above then does the /discover hop).
  useEffect(() => {
    if (linkedinStatus === "verified" && refetch) {
      refetch();
    }
  }, [linkedinStatus, refetch]);

  const logout = async () => {
    try { await axios.post(`${BACKEND_URL}/api/employer/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
    window.location.href = "/employer/login";
  };

  const startLinkedIn = async () => {
    setLiBusy(true);
    setLiError("");
    try {
      const r = await axios.post(`${BACKEND_URL}/api/employer/linkedin/start`,
        {}, { withCredentials: true });
      if (r.data.already_verified) {
        navigate("/discover", { replace: true });
        return;
      }
      if (!r.data.auth_url) {
        setLiError("Couldn't start LinkedIn verification. Please retry.");
        return;
      }
      // Hand the browser over to LinkedIn.
      window.location.href = r.data.auth_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't start LinkedIn verification.";
      setLiError(msg);
      setLiBusy(false);
    }
  };

  // Clear the toast query params after a beat so a page refresh doesn't
  // keep re-rendering the same success/failure toast.
  useEffect(() => {
    if (!linkedinStatus) return undefined;
    const t = setTimeout(() => {
      setParams((cur) => {
        const next = new URLSearchParams(cur);
        next.delete("linkedin");
        next.delete("reason");
        return next;
      }, { replace: true });
    }, 6000);
    return () => clearTimeout(t);
  }, [linkedinStatus, setParams]);

  if (loading) return null;

  return (
    <div className="min-h-screen py-10 px-4"
         style={{ background: "linear-gradient(180deg, #FFFBEB 0%, #FEF3C7 100%)" }}>
      <div className="max-w-lg mx-auto">
        {/* LinkedIn callback toast */}
        {linkedinStatus === "verified" && (
          <div className="rounded-md p-3 mb-4 flex items-start gap-2"
               style={{ background: "#DCFCE7", border: "1.5px solid #10B981", color: "#065F46" }}
               data-testid="linkedin-toast-verified">
            <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-0.5" style={{ fontWeight: 700 }}>
                · Verified via LinkedIn
              </div>
              <div className="text-[13px]">{bannerCopy}</div>
              <div className="text-[11px] opacity-80 mt-1">Redirecting you to the Talent Directory…</div>
            </div>
          </div>
        )}
        {(linkedinStatus === "pending_review" || linkedinStatus === "error") && (
          <div className="rounded-md p-3 mb-4 flex items-start gap-2"
               style={{
                 background: linkedinStatus === "error" ? "#FEE2E2" : "#FEF3C7",
                 border: `1.5px solid ${linkedinStatus === "error" ? "#EF4444" : "#F59E0B"}`,
                 color: linkedinStatus === "error" ? "#7F1D1D" : "#78350F",
               }}
               data-testid={`linkedin-toast-${linkedinStatus}`}>
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-0.5" style={{ fontWeight: 700 }}>
                {linkedinStatus === "error" ? "· LinkedIn verification error" : "· Still pending"}
              </div>
              <div className="text-[13px]">{bannerCopy}</div>
            </div>
          </div>
        )}

        <div className="rounded-md p-6 md:p-8 text-center"
             style={{ background: "white", border: "2px solid #0F1B2D", boxShadow: "6px 6px 0 0 #FFC145" }}
             data-testid="employer-pending-card">
          <div className="inline-flex items-center justify-center mb-3"
               style={{
                 width: 60, height: 60, borderRadius: "50%",
                 background: "#FEF3C7", border: "2px dashed #FFC145",
               }}>
            <Clock size={28} style={{ color: "#CA8A04" }} />
          </div>
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
               style={{ color: "#CA8A04", fontWeight: 700 }}>
            · Verification in progress
          </div>
          <h1 className="font-display uppercase leading-tight mb-3"
              style={{ fontSize: "clamp(1.4rem, 2.6vw, 1.9rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
            Thanks, {employer?.company || "recruiter"} —<br />we&apos;re double-checking your account.
          </h1>
          <p className="text-sm leading-relaxed mb-4 max-w-md mx-auto" style={{ color: "#334155" }}>
            An admin will review your signup within one business day. When you&apos;re
            verified, the Resmo Talent Directory unlocks and you&apos;ll be able to
            search candidates by role, skills, and location.
          </p>

          {/* Fast-lane: verify with LinkedIn */}
          <div className="rounded p-4 mb-4"
               style={{ background: "#F0F9FF", border: `1.5px solid #0A66C2`, textAlign: "left" }}
               data-testid="linkedin-fastlane">
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                 style={{ color: "#0A66C2", fontWeight: 800 }}>
              · Fast-track · Skip the wait
            </div>
            <div className="text-[13px] mb-3" style={{ color: "#0F1B2D" }}>
              Sign in with LinkedIn and we&apos;ll auto-verify you if your LinkedIn
              email matches your company domain. Takes 15 seconds.
            </div>
            <button
              type="button"
              onClick={startLinkedIn}
              disabled={liBusy}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 font-mono-rm uppercase text-[11px] tracking-widest"
              style={{
                background: "#0A66C2", color: "white",
                border: `2px solid #0F1B2D`,
                boxShadow: `3px 3px 0 0 #0F1B2D`,
                fontWeight: 800, cursor: liBusy ? "wait" : "pointer",
                opacity: liBusy ? 0.7 : 1,
              }}
              data-testid="linkedin-verify-btn">
              {liBusy ? <Loader size={12} className="animate-spin" /> : <Linkedin size={13} />}
              {liBusy ? "Redirecting…" : "Verify with LinkedIn"}
            </button>
            {liError && (
              <div className="text-[11px] mt-2" style={{ color: "#7F1D1D" }}
                   data-testid="linkedin-start-err">
                {liError}
              </div>
            )}
          </div>

          <div className="rounded p-3 mb-4 text-left inline-block"
               style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1", minWidth: 260 }}>
            <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-1"
                 style={{ color: "#94A3B8", fontWeight: 700 }}>· What we&apos;re checking</div>
            <ul className="text-[12px] leading-relaxed" style={{ color: "#334155" }}>
              <li>· Work email domain owned by {employer?.company || "your company"}</li>
              <li>· LinkedIn profile matches a real recruiter</li>
              <li>· No blocklist flags on the company</li>
            </ul>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button onClick={logout}
                    className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-[11px] font-mono-rm uppercase tracking-widest"
                    style={{ background: "#F8FAFC", color: "#0F1B2D", border: "1.5px solid #0F1B2D", fontWeight: 700 }}
                    data-testid="employer-pending-logout">
              <LogOut size={12} /> Sign out
            </button>
            <Link to="/" className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-[11px] font-mono-rm uppercase tracking-widest"
                  style={{ background: "#0F1B2D", color: "#FFC145", border: "1.5px solid #0F1B2D", fontWeight: 700 }}
                  data-testid="employer-pending-home">
              <ShieldCheck size={12} /> Resmo home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
